import { inject } from '@angular/core';
import { PAGINATION_DEFAULT } from '@core/constants';
import {
  AuditLogEntry,
  AuditLogResponseModel,
  NewPartnership,
  Partnership,
  PartnershipFilterModel,
  PartnershipsResponseModel,
  PartnershipSummary,
} from '@core/models';
import { AuditLogService, PartnershipService } from '@core/services';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';

type PartnershipState = {
  isLoading: boolean;
  data: PartnershipsResponseModel;
  filter: PartnershipFilterModel;
  isLoadingDetail: boolean;
  detail: Partnership | undefined;
  /** An accept/decline/extend/revoke action is in flight. */
  isProcessing: boolean;
  isLoadingAuditLog: boolean;
  auditLog: AuditLogEntry[];
  auditLogCount: number;
  /** Pending invitations the active partner can accept (guest role, Pending status). */
  pendingPartnerships: PartnershipSummary[];
  pendingCount: number;
};

const initialState: PartnershipState = {
  isLoading: false,
  data: { numberOfItems: 0, partnerships: [] },
  filter: {
    limit: PAGINATION_DEFAULT.limit,
    offset: PAGINATION_DEFAULT.offset,
  },
  isLoadingDetail: false,
  detail: undefined,
  isProcessing: false,
  isLoadingAuditLog: false,
  auditLog: [],
  auditLogCount: 0,
  pendingPartnerships: [],
  pendingCount: 0,
};

export const PartnershipStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      partnershipService = inject(PartnershipService),
      auditLogService = inject(AuditLogService),
    ) => ({
      getPartnerships: rxMethod<PartnershipFilterModel>(
        pipe(
          tap(filter => patchState(store, { isLoading: true, filter })),
          switchMap(filter =>
            partnershipService.getPartnerships(filter).pipe(
              tapResponse({
                next: (data: PartnershipsResponseModel) => {
                  patchState(store, { data, isLoading: false });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            ),
          ),
        ),
      ),

      /**
       * Loads the pending invitations the active partner can act on (guest role,
       * Pending status) into a dedicated slice, so the header badge count stays
       * independent of the paginated list in `data`.
       */
      getPendingPartnerships: rxMethod<void>(
        pipe(
          switchMap(() =>
            partnershipService
              .getPartnerships({
                role: 'guest',
                status: 'Pending',
                limit: PAGINATION_DEFAULT.limit,
                offset: PAGINATION_DEFAULT.offset,
              })
              .pipe(
                tapResponse({
                  next: (data: PartnershipsResponseModel) => {
                    patchState(store, {
                      pendingPartnerships: data.partnerships,
                      pendingCount: data.numberOfItems,
                    });
                  },
                  error: () => {},
                }),
              ),
          ),
        ),
      ),

      createPartnership: rxMethod<NewPartnership>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(payload =>
            partnershipService.createPartnership(payload).pipe(
              switchMap(() => partnershipService.getPartnerships(store.filter())),
              tapResponse({
                next: (data: PartnershipsResponseModel) => {
                  patchState(store, { data, isLoading: false });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            ),
          ),
        ),
      ),

      getPartnership: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isLoadingDetail: true, detail: undefined })),
          switchMap(partnershipOid =>
            partnershipService.getPartnership(partnershipOid).pipe(
              tapResponse({
                next: (detail: Partnership) => {
                  patchState(store, { detail, isLoadingDetail: false });
                },
                error: () => {
                  patchState(store, { isLoadingDetail: false });
                },
              }),
            ),
          ),
        ),
      ),

      getAuditLog: rxMethod<{ partnershipOid: string; limit?: number; offset?: number }>(
        pipe(
          tap(() => patchState(store, { isLoadingAuditLog: true })),
          switchMap(({ partnershipOid, limit, offset }) =>
            auditLogService.listAuditLog({ partnershipOid, limit, offset }).pipe(
              tapResponse({
                next: (data: AuditLogResponseModel) => {
                  patchState(store, {
                    auditLog: data.entries,
                    auditLogCount: data.numberOfItems,
                    isLoadingAuditLog: false,
                  });
                },
                error: () => {
                  patchState(store, { isLoadingAuditLog: false });
                },
              }),
            ),
          ),
        ),
      ),

      acceptPartnership: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isProcessing: true })),
          switchMap(partnershipOid =>
            partnershipService.acceptPartnership(partnershipOid).pipe(
              tapResponse({
                next: (detail: Partnership) => patchState(store, { detail, isProcessing: false }),
                error: () => patchState(store, { isProcessing: false }),
              }),
            ),
          ),
        ),
      ),

      declinePartnership: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isProcessing: true })),
          switchMap(partnershipOid =>
            partnershipService.declinePartnership(partnershipOid).pipe(
              tapResponse({
                next: (detail: Partnership) => patchState(store, { detail, isProcessing: false }),
                error: () => patchState(store, { isProcessing: false }),
              }),
            ),
          ),
        ),
      ),

      extendPartnership: rxMethod<{ partnershipOid: string; reviewDueDate: string }>(
        pipe(
          tap(() => patchState(store, { isProcessing: true })),
          switchMap(({ partnershipOid, reviewDueDate }) =>
            partnershipService.extendPartnership(partnershipOid, reviewDueDate).pipe(
              tapResponse({
                next: (detail: Partnership) => patchState(store, { detail, isProcessing: false }),
                error: () => patchState(store, { isProcessing: false }),
              }),
            ),
          ),
        ),
      ),

      revokePartnership: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isProcessing: true })),
          switchMap(partnershipOid =>
            partnershipService.revokePartnership(partnershipOid).pipe(
              tapResponse({
                next: (detail: Partnership) => patchState(store, { detail, isProcessing: false }),
                error: () => patchState(store, { isProcessing: false }),
              }),
            ),
          ),
        ),
      ),

      addInstallationGroup: rxMethod<{ partnershipOid: string; installationGroupOid: string }>(
        pipe(
          tap(() => patchState(store, { isProcessing: true })),
          switchMap(({ partnershipOid, installationGroupOid }) =>
            partnershipService
              .addInstallationGroupToPartnership(partnershipOid, installationGroupOid)
              .pipe(
                // Re-fetch the detail so the linked installation groups list updates.
                switchMap(() => partnershipService.getPartnership(partnershipOid)),
                tapResponse({
                  next: (detail: Partnership) => patchState(store, { detail, isProcessing: false }),
                  error: () => patchState(store, { isProcessing: false }),
                }),
              ),
          ),
        ),
      ),

      removeInstallationGroup: rxMethod<{ partnershipOid: string; installationGroupOid: string }>(
        pipe(
          tap(() => patchState(store, { isProcessing: true })),
          switchMap(({ partnershipOid, installationGroupOid }) =>
            partnershipService
              .removeInstallationGroupFromPartnership(partnershipOid, installationGroupOid)
              .pipe(
                switchMap(() => partnershipService.getPartnership(partnershipOid)),
                tapResponse({
                  next: (detail: Partnership) => patchState(store, { detail, isProcessing: false }),
                  error: () => patchState(store, { isProcessing: false }),
                }),
              ),
          ),
        ),
      ),
    }),
  ),
);
